import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import {
  Instagram as InstagramIcon,
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { UnifiedFeedback } from '../../types/unifiedFeedback';
import { getFeedbackExternalLabel, getFeedbackExternalUrl } from '../../utils/feedbackUrls';
import { DMIcon, GCCIcon, TikTokIcon } from './PlatformIcons';

export interface FeedbackDetailDialogProps {
  open: boolean;
  feedback: UnifiedFeedback | null;
  onClose: () => void;
  onViewGcc?: (feedback: UnifiedFeedback) => void;
}

const getPlatformChip = (platform: UnifiedFeedback['platform']) => {
  switch (platform) {
    case 'instagram':
      return <Chip icon={<InstagramIcon />} label="Instagram" size="small" color="secondary" />;
    case 'google':
      return <Chip icon={<GoogleIcon />} label="Google Review" size="small" color="primary" />;
    case 'tiktok':
      return <Chip icon={<TikTokIcon />} label="TikTok" size="small" />;
    case 'facebook':
      return <Chip icon={<FacebookIcon />} label="Facebook" size="small" sx={{ bgcolor: '#1877F2', color: '#fff' }} />;
    case 'gcc':
      return <Chip icon={<GCCIcon />} label="GCC" size="small" color="primary" />;
    case 'dm':
      return <Chip icon={<DMIcon />} label="Direct Message" size="small" color="info" />;
    default:
      return null;
  }
};

const getStatusChip = (status?: string) => {
  if (!status) return <Chip size="small" label="New" color="info" />;
  const label = status.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const color =
    status === 'resolved'
      ? 'success'
      : status === 'in_progress'
        ? 'warning'
        : status === 'not_resolved'
          ? 'error'
          : 'info';
  return <Chip size="small" label={label} color={color} />;
};

export const FeedbackDetailDialog: React.FC<FeedbackDetailDialogProps> = ({
  open,
  feedback,
  onClose,
  onViewGcc,
}) => {
  if (!feedback) {
    return null;
  }

  const externalUrl = getFeedbackExternalUrl(feedback);
  const isGcc = feedback.platform === 'gcc' && feedback.brand_identifier;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Feedback Details</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar src={feedback.avatar_thumbnail || feedback.authorImage || feedback.author_image}>
              {feedback.author?.[0]}
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6">{feedback.author}</Typography>
              <Typography variant="caption" color="text.secondary">
                {dayjs(feedback.timestamp || feedback.created_at).format('MMMM D, YYYY h:mm A')}
              </Typography>
            </Box>
            {getPlatformChip(feedback.platform)}
          </Box>

          <Box display="flex" flexWrap="wrap" gap={1}>
            {feedback.sentiment && (
              <Chip label={`Sentiment: ${feedback.sentiment}`} size="small" />
            )}
            {feedback.category && <Chip label={`Category: ${feedback.category}`} size="small" color="info" />}
            {feedback.priority != null && feedback.priority !== '' && (
              <Chip label={`Priority: ${feedback.priority}`} size="small" />
            )}
            {getStatusChip(feedback.complaint_status)}
            {feedback.brand_name && <Chip label={feedback.brand_name} size="small" variant="outlined" />}
          </Box>

          {feedback.rating != null && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Rating
              </Typography>
              <Rating value={feedback.rating} readOnly />
            </Box>
          )}

          <Divider />

          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {feedback.text || 'No content'}
          </Typography>

          {feedback.suggested_reply && (
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Suggested Reply
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {feedback.suggested_reply}
              </Typography>
            </Box>
          )}

          {feedback.complaint_note && (
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Complaint Note
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {feedback.complaint_note}
              </Typography>
            </Box>
          )}

          {feedback.platform === 'gcc' && feedback.brand_identifier && (
            <Typography variant="body2" color="text.secondary">
              Reference: {feedback.brand_identifier}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {externalUrl && (
          <Button
            component={Link}
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<LaunchIcon />}
          >
            {getFeedbackExternalLabel(feedback)}
          </Button>
        )}
        {isGcc && onViewGcc && (
          <Button startIcon={<LaunchIcon />} onClick={() => onViewGcc(feedback)}>
            View GCC details
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
